import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Award, Target, Handshake, TrendingUp } from 'lucide-react';

const ZohoPartner = () => {
  const partnerBenefits = [
    {
      icon: <Award className="w-8 h-8 text-blue-700" />,
      title: 'Gecertificeerde Expertise',
      description: 'Onze consultants zijn officieel gecertificeerd door Zoho en blijven continu hun kennis updaten.',
    },
    {
      icon: <Target className="w-8 h-8 text-blue-700" />,
      title: 'Directe Support',
      description: 'Als partner hebben wij directe toegang tot Zoho support en engineering teams.',
    },
    {
      icon: <Handshake className="w-8 h-8 text-blue-700" />,
      title: 'Best Practices',
      description: 'Wij volgen de aanbevolen implementatiemethoden van Zoho voor optimale resultaten.',
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-700" />,
      title: 'Vroege Toegang',
      description: 'Wij krijgen vroege toegang tot nieuwe features en kunnen deze voor u implementeren.',
    },
  ];

  const zohoProducts = [
    {
      name: 'Zoho CRM',
      description: 'Complete CRM oplossing voor sales, marketing en support',
      link: '/zoho-crm',
    },
    {
      name: 'Zoho Desk',
      description: 'Helpdesk software voor uitmuntende klantenservice',
      link: '/zoho-desk',
    },
    {
      name: 'Zoho Books',
      description: 'Online boekhoudsoftware voor financieel beheer',
      link: '/zoho-books',
    },
    {
      name: 'Zoho Projects',
      description: 'Projectmanagement tool voor teams',
      link: '/zoho-projects',
    },
    {
      name: 'Zoho People',
      description: 'HR management systeem',
      link: '/zoho-people',
    },
    {
      name: 'Zoho Vault',
      description: 'Veilige password manager voor teams',
      link: '/zoho-vault',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              Officieel Zoho Partner
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Uw Gecertificeerde Zoho Partner in Nederland
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Als officiële Zoho partner bieden wij expert implementaties, migraties en ondersteuning 
              voor alle Zoho producten. Wij helpen uw bedrijf maximaal te profiteren van het Zoho ecosysteem.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  Gratis Intakegesprek
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
              
              <a href="tel:0638823471">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                >
                  Bel: 06 3882 3471
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What Does Partner Mean Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat Betekent het om Zoho Partner te zijn?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Als officiële Zoho partner heeft Guijs Consultancy een directe relatie met Zoho Corporation. 
                Dit betekent dat wij gecertificeerd zijn, regelmatig training ontvangen, en toegang hebben tot 
                exclusieve resources en support die niet beschikbaar zijn voor gewone gebruikers.
              </p>
              <p>
                Ons partnerschap stelt ons in staat om u de beste service te bieden bij het implementeren, 
                configureren en optimaliseren van Zoho producten. Wij kennen de ins en outs van elk product 
                en weten precies hoe deze in te zetten voor uw specifieke business case.
              </p>
              <p>
                Bovendien hebben wij als partner toegang tot pre-release versies van nieuwe functionaliteiten, 
                zodat wij u altijd kunnen adviseren over de nieuwste mogelijkheden en ontwikkelingen binnen 
                het Zoho ecosysteem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Voordelen van Werken met een Zoho Partner
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ontdek wat het voor u betekent om samen te werken met een gecertificeerde Zoho partner.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partnerBenefits.map((benefit, index) => (
              <Card key={benefit.title} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zoho Products We Implement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Zoho Producten die Wij Implementeren
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Van CRM tot HR - wij beheersen het volledige Zoho ecosysteem en helpen u de juiste tools te kiezen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {zohoProducts.map((product, index) => (
              <Card key={`item-${index}`} className="border-2 border-gray-200 hover:border-blue-500 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <Link 
                    to={product.link}
                    className="text-blue-700 hover:text-blue-800 font-semibold inline-flex items-center group"
                  >
                    Meer informatie
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_crm-consulting-1/artifacts/z9uouqpi_Zoho%20Partner%20Nederland.png"
                alt="Zoho Partner Nederland"
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Waarom Guijs Consultancy als Uw Zoho Partner?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Wij zijn meer dan een implementatiepartner - wij zijn uw strategische adviseur die 
                zorgt dat u maximaal profiteert van uw Zoho investering.
              </p>

              <ul className="space-y-4">
                {[
                  'Jarenlange ervaring met Zoho implementaties',
                  'Team van gecertificeerde Zoho consultants',
                  'Focus op Nederlandse markt en bedrijven',
                  'Persoonlijke aanpak en directe communicatie',
                  'Betaalbare tarieven zonder verborgen kosten',
                  'Continue support en optimalisatie na go-live',
                ].map((item, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Onze Aanpak als Zoho Partner
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center">
              Wij volgen een bewezen methodiek die zorgt voor succesvolle implementaties.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1. Analyse & Advies</h3>
                  <p className="text-gray-600">
                    Wij starten met een grondige analyse van uw processen en adviseren welke Zoho 
                    producten het beste passen bij uw organisatie.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2. Maatwerk Implementatie</h3>
                  <p className="text-gray-600">
                    Wij configureren de Zoho producten precies volgens uw wensen en bedrijfsprocessen, 
                    inclusief alle integraties.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3. Training & Adoptie</h3>
                  <p className="text-gray-600">
                    Wij trainen uw team grondig en zorgen voor een soepele adoptie van de nieuwe systemen 
                    binnen uw organisatie.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4. Support & Optimalisatie</h3>
                  <p className="text-gray-600">
                    Ook na go-live blijven wij betrokken met doorlopende support en regelmatige 
                    optimalisaties van uw Zoho omgeving.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar om te Starten met Zoho?"
        subtitle="Neem contact op met uw lokale Zoho partner en ontdek de mogelijkheden."
      />
    </div>
  );
};

export default ZohoPartner;
